/* Allergen introduction planner (sections/ob-res-allergens.liquid).
   1. "About your baby" answers -> guidance card, following NIAID 2017 / 2021 consensus / ASCIA 2026.
   2. Top 9 allergen tracker, stored in this browser only (localStorage, key obr-allergens-v1). */
(() => {
  'use strict';
  const KEY = 'obr-allergens-v1';
  const DAY = 86400000;

  const store = {
    read() { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; } },
    write(v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode: tracker still works for this visit */ } },
  };
  let saved = store.read();
  saved.foods = saved.foods || {};

  /* ---------- 1. Guidance ---------- */
  function initPlan(root) {
    const form = root.querySelector('[data-obr-plan-form]');
    const range = form.querySelector('[name="age"]');
    const val = form.querySelector('[data-age-val]');
    const unit = form.querySelector('[data-age-unit]');
    const card = root.querySelector('[data-result-card]');
    const title = root.querySelector('[data-result-title]');
    const when = root.querySelector('[data-result-when]');
    const body = root.querySelector('[data-result-body]');
    const points = root.querySelector('[data-result-points]');

    if (saved.answers) {
      Object.entries(saved.answers).forEach(([k, v]) => {
        const el = form.querySelector(`[name="${k}"][value="${v}"]`) || (k === 'age' ? range : null);
        if (!el) return;
        if (el.type === 'radio') el.checked = true; else el.value = v;
      });
    }

    form.querySelectorAll('[data-age-step]').forEach((b) => b.addEventListener('click', () => {
      range.value = Math.min(24, Math.max(0, +range.value + +b.dataset.ageStep));
      update();
    }));
    form.addEventListener('input', update);
    form.addEventListener('change', update);
    form.addEventListener('submit', (e) => e.preventDefault());

    function update() {
      const fd = new FormData(form);
      const a = Object.fromEntries(fd.entries());
      const age = +a.age;
      val.textContent = age;
      unit.textContent = age === 1 ? 'month' : 'months';
      range.style.setProperty('--fill', (age / 24) * 100 + '%');
      saved.answers = a;
      store.write(saved);

      let tone = 'low';
      let t = 'Introduce allergens with your baby’s first foods';
      let w = 'Around 6 months, with other first foods';
      let b = 'With no eczema or food allergy, peanut, egg and the other common allergens can go in freely alongside other first foods.';
      const list = [];

      if (a.eczema === 'severe' || a.allergy === 'egg') {
        tone = 'high';
        t = 'Talk to your pediatrician before peanut';
        w = 'As early as 4 to 6 months, after that conversation';
        b = (a.allergy === 'egg' ? 'An egg allergy' : 'Severe eczema') + ' raises the chance of peanut allergy. Guidelines still say to start peanut early, but your pediatrician may suggest a test or a first feeding in the office.';
        list.push('Start a few other solids first, to show your baby is ready.');
        list.push('Ask about peanut before 6 months if you can. Waiting longer does not lower the risk.');
        if (a.allergy === 'egg') list.push('Keep egg out of the diet unless your doctor says otherwise, and keep introducing the other allergens.');
        else list.push('Well-cooked egg is worth introducing early too: ask about it at the same visit.');
      } else if (a.eczema === 'mild') {
        tone = 'mid';
        t = 'Start peanut at home around 6 months';
        w = 'Around 6 months, after a few first foods';
        b = 'With mild or moderate eczema, guidelines suggest introducing peanut around 6 months at home, once your baby has managed a few other solids. No test is needed first.';
        list.push('Introduce well-cooked egg around the same time.');
      } else if (a.allergy === 'other') {
        tone = 'mid';
        t = 'Keep introducing the other allergens';
        w = 'Around 6 months, with other first foods';
        b = 'With one diagnosed food allergy, keep that food out but carry on introducing the rest (ASCIA 2026). Ask your pediatrician whether any of them need extra care.';
      }

      if (age < 4) list.unshift('It is too early for solids. Wait until at least 4 months; most babies are ready around 6 months.');
      else if (a.solids === 'no' && age < 6) list.unshift('Watch for readiness: sitting with support, steady head control, opening the mouth for food and swallowing instead of pushing it out.');
      else if (a.solids === 'no') list.unshift('Your baby is old enough to start solids now, with allergens among the first foods.');
      else if (age >= 12) list.unshift('It is not too late: introduce any allergens your baby has not had yet.');
      else list.unshift('Your baby already eats solids, so allergens can come in now, one new allergen per meal.');

      if (a.family === 'yes' && tone === 'low') list.push('A family history of allergy on its own does not change the timing. Eczema in your baby is the bigger risk factor.');

      card.className = 'obr-result obr-result--' + tone;
      title.textContent = t;
      when.textContent = w;
      body.textContent = b;
      points.innerHTML = list.map((p) => `<li>${p}</li>`).join('');
    }
    update();
  }

  /* ---------- 2. Tracker ---------- */
  const STATUS = { todo: 'Not tried yet', tried: 'Tried once', diet: 'In the diet', reaction: 'Reaction noted' };

  function initTracker(root) {
    const foods = [...root.querySelectorAll('[data-food]')];
    const done = root.querySelector('[data-track-done]');
    const meter = root.querySelector('[data-track-meter]');

    function ago(ts) {
      const d = Math.floor((startOfDay(Date.now()) - startOfDay(ts)) / DAY);
      return d <= 0 ? 'today' : d === 1 ? 'yesterday' : d + ' days ago';
    }
    function startOfDay(ts) { const x = new Date(ts); x.setHours(0, 0, 0, 0); return x.getTime(); }

    function paint(li) {
      const k = li.dataset.food;
      const f = saved.foods[k] || { state: 'todo' };
      li.dataset.state = f.state;
      li.querySelector('[data-food-status]').textContent = STATUS[f.state];
      li.querySelectorAll('[data-set]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.set === f.state ? 'true' : 'false'));
      const last = li.querySelector('[data-last]');
      const due = f.last && f.state === 'diet' && Date.now() - startOfDay(f.last) > 7 * DAY;
      li.classList.toggle('is-due', !!due);
      last.textContent = f.last ? (due ? `Due again: last given ${ago(f.last)}` : `Last given ${ago(f.last)}`) : '';
      li.querySelector('[data-given]').disabled = f.state === 'reaction';
      li.querySelector('[data-warn]').hidden = f.state !== 'reaction';
    }
    function paintAll() {
      foods.forEach(paint);
      const n = foods.filter((li) => (saved.foods[li.dataset.food] || {}).state === 'diet').length;
      done.textContent = n;
      meter.style.width = (n / foods.length) * 100 + '%';
    }

    foods.forEach((li) => {
      const k = li.dataset.food;
      li.querySelectorAll('[data-set]').forEach((b) => b.addEventListener('click', () => {
        const f = saved.foods[k] || { state: 'todo' };
        f.state = f.state === b.dataset.set ? 'todo' : b.dataset.set;
        saved.foods[k] = f;
        store.write(saved);
        paintAll();
      }));
      li.querySelector('[data-given]').addEventListener('click', () => {
        const f = saved.foods[k] || { state: 'todo' };
        f.last = Date.now();
        if (f.state === 'todo') f.state = 'tried';
        else if (f.state === 'tried') f.state = 'diet';
        saved.foods[k] = f;
        store.write(saved);
        paintAll();
      });
    });

    const clear = root.querySelector('[data-track-clear]');
    if (clear) clear.addEventListener('click', () => {
      if (!window.confirm('Clear the tracker for all 9 allergens?')) return;
      saved.foods = {};
      store.write(saved);
      paintAll();
    });
    paintAll();
  }

  function boot() {
    document.querySelectorAll('[data-obr-plan]').forEach(initPlan);
    document.querySelectorAll('[data-obr-tracker]').forEach(initTracker);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
